import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

/**
 * Entitlements layer (Stage 2 of the payments migration).
 *
 * This is the ONLY client-side reader of what a company may do. It never reads plan
 * names, product ids or provider state — the database resolver (`get_my_entitlements`)
 * collapses plan grid + admin overrides + quota usage into one answer, so the billing
 * provider can change underneath without touching a single feature check.
 *
 * Phase A runs in SHADOW mode: nothing is gated on these values yet. `require_entitlement`
 * on the server logs would-be denials instead of raising, so we can compare the new grid
 * against live behaviour before any user loses a habit rung they already rely on.
 */

export type FeatureKey =
  | 'menus.browse'
  | 'messages.send'
  | 'inventory.manage'
  | 'inventory.resell'
  | 'menus.manage'
  | 'insights.view'
  | 'alerts.watchlists'
  | 'buyer.requests';

type EntitlementRecord = {
  enabled: boolean;
  limit: number | null;
  quota: number | null;
  quota_period: string | null;
  remaining: number | null;
  used: number;
  source: 'plan' | 'override';
  status: string;
};

type EntitlementPayload = {
  company_id: string | null;
  revision: number | null;
  features: Partial<Record<string, EntitlementRecord>>;
};

const emptyPayload: EntitlementPayload = { company_id: null, revision: null, features: {} };

export function useEntitlements() {
  const { user } = useAuth();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['entitlements', user?.id],
    queryFn: async (): Promise<EntitlementPayload> => {
      const { data, error } = await supabase.rpc('get_my_entitlements');
      if (error) throw error;
      return (data as unknown as EntitlementPayload) ?? emptyPayload;
    },
    enabled: !!user,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const payload = data ?? emptyPayload;

  /** Unknown ≠ denied. Callers must treat `isError` as "ask again", never as "no plan". */
  const can = (feature: FeatureKey): boolean => payload.features[feature]?.enabled ?? false;
  const isUnknown = isLoading || !!error;
  /**
   * "We don't have an answer yet" — loading, failed, or a payload with no company /
   * no features at all (e.g. resolved a moment before the workspace existed). Gates must
   * never render a paywall on this: a plan the person just chose would look unpaid.
   */
  const isUnresolved =
    isUnknown || payload.company_id === null || Object.keys(payload.features).length === 0;
  /**
   * For presentation only: while we haven't resolved yet, show the capability rather
   * than hide it. Hiding a nav item a seller uses every morning is a habit break;
   * the server guard is what actually enforces.
   */
  const canOrUnknown = (feature: FeatureKey): boolean => (isUnknown ? true : can(feature));
  const limitOf = (feature: FeatureKey): number | null => payload.features[feature]?.limit ?? null;
  const quotaRemaining = (feature: FeatureKey): number | null =>
    payload.features[feature]?.remaining ?? null;
  const sourceOf = (feature: FeatureKey): 'plan' | 'override' | null =>
    payload.features[feature]?.source ?? null;

  return {
    isLoading,
    isError: !!error,
    isUnknown,
    isUnresolved,
    error,
    refetch,
    revision: payload.revision,
    features: payload.features,
    can,
    canOrUnknown,
    limitOf,
    quotaRemaining,
    sourceOf,
  };
}
